import {useQuery} from '@tanstack/react-query'
import {useAuthUser} from 'react-auth-kit'
import {API} from 'src/feature/bite/domain'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import useRequestParams from 'src/feature/bite/hook/request'

const apiUrlRedeem = `${API}/Analytics`

export const useGetMemberAnalytics = (period: number = 1) => {
  const {headers} = useRequestParams()
  const auth = useAuthUser()
  const userID = auth()?.id

  return useQuery(
    ['memberAnalytics', period],
    async () => {
      const reqBody = {
        language: headers.Language,
        userID,
        period,
      }

      const response = await fetch(`${apiUrlRedeem}/GetMemberAnalytics`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reqBody),
      })

      const data = await response.json()
      return data.response
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
    }
  )
}

export const useGetAverageAnalytics = (period: number = 1) => {
  const {headers} = useRequestParams()
  const auth = useAuthUser()
  const userID = auth()?.id

  return useQuery(
    ['averageAnalytics', period],
    async () => {
      const reqBody = {
        userID,
        period,
        language: headers.Language,
      }

      const response = await fetch(`${apiUrlRedeem}/GetAverageAnalytics`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reqBody),
      })

      const data = await response.json()
      return data.response
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
    }
  )
}

export const useGetRateAnalytics = (period: number = 1) => {
  const {headers} = useRequestParams()
  const auth = useAuthUser()
  const userID = auth()?.id

  return useQuery(
    ['rateAnalytics', period],
    async () => {
      const reqBody = {
        userID,
        period,
        language: headers.Language,
      }

      const response = await fetch(`${apiUrlRedeem}/GetRateAnalytics`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reqBody),
      })

      const data = await response.json()
      return data.response
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
    }
  )
}

export const useGetPointAnalytics = (period: number = 1) => {
  const {headers} = useRequestParams()
  const auth = useAuthUser()
  const userID = auth()?.id

  return useQuery(
    ['pointAnalytics', period],
    async () => {
      const reqBody = {
        userID,
        period,
        language: headers.Language,
      }

      const response = await fetch(`${apiUrlRedeem}/GetPointAnalytics`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reqBody),
      })

      const data = await response.json()
      return data.response
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
    }
  )
}

export const useGetRevenueTimeBandAnalytics = (period: number = 1) => {
  const {headers} = useRequestParams()
  const auth = useAuthUser()
  const userID = auth()?.id

  return useQuery(
    ['revenueTimeBandAnalytics', period],
    async () => {
      const reqBody = {
        userID,
        period,
        language: headers.Language,
      }

      const response = await fetch(`${apiUrlRedeem}/GetRevenueTimeBandAnalytics`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reqBody),
      })

      const data = await response.json()
      return data.response
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
    }
  )
}

export const useGetRevenueAnalytics = (period: number = 1) => {
  const {headers} = useRequestParams()
  const auth = useAuthUser()
  const userID = auth()?.id

  return useQuery(
    ['revenueAnalytics', period],
    async () => {
      const reqBody = {
        userID,
        period,
        language: headers.Language,
      }

      const response = await fetch(`${apiUrlRedeem}/GetRevenueAnalytics`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reqBody),
      })

      const data = await response.json()
      return data.response
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
    }
  )
}

export const useGetOrderAnalytics = (period: number = 1) => {
  const {headers} = useRequestParams()
  const auth = useAuthUser()
  const userID = auth()?.id

  return useQuery(
    ['orderAnalytics', period],
    async () => {
      const reqBody = {
        userID,
        period,
        language: headers.Language,
      }

      const response = await fetch(`${apiUrlRedeem}/GetOrderAnalytics`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reqBody),
      })

      const data = await response.json()
      return data.response
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
    }
  )
}

export const useGetTransactionAnalytics = (period: number = 1) => {
  const {headers} = useRequestParams()
  const auth = useAuthUser()
  const userID = auth()?.id

  return useQuery(
    ['transactionAnalytics', period],
    async () => {
      const timeZoneOffset = getTimeZone()

      const reqBody = {
        userID,
        period,
        language: headers.Language,
        timeZoneOffset,
      }

      const response = await fetch(`${apiUrlRedeem}/GetTransactionAnalytics`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reqBody),
      })

      const data = await response.json()
      return data.response
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
    }
  )
}
